import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { setup_product_requirements } from "services/apiSrv";
import { show_toast } from "utils/helpers";
import { useParams } from "react-router-dom";

// Validation schema
const validationSchema = Yup.object().shape({
  requirementId: Yup.array()
    .min(1, "At least one requirement must be selected")
    .required("Requirement is required"),
});

export default function AddRiskRequirement({ refetchProductDetails, requirementsItems }) {
  const { id: productSystemRef } = useParams();

  const riskOptions = [
    { id: "SFF00414820", name: "Has Any Insurer or Company Declined to Insure You?", description: "If Any Insurer Has Declined to Insure", isRequired: true, isUnique: false, validation: "MANUAL" },
    { id: "SFF00424820", name: "Has Any Insurer or Company Cancelled or Refused to Renew Your Policy?", description: "If Any Insurer Has Cancelled/Failed to Renew Your Policy", isRequired: true, isUnique: false, validation: "MANUAL" },
    { id: "SFF00434820", name: "Has Any Insurer or Company Increased Your Premium on Renewal", description: "If Any Insurer or Company Has Increased Your Premium on Renewal", isRequired: true, isUnique: false, validation: "MANUAL" },
    { id: "SFF00444820", name: "Has Any Insurer or Company Required Special Terms to Insure You", description: "If Any Insurer Has Required Special Terms to Insure You", isRequired: true, isUnique: false, validation: "MANUAL" },
    { id: "SFF01834920", name: "Vehicle Registration Number", description: "Vehicles registration number", isRequired: true, isUnique: true, validation: "MANUAL" },
    { id: "SFF01914920", name: "Chassis Number", description: "Vehicle Chassis Number", isRequired: true, isUnique: true, validation: "MANUAL" },
    { id: "SFF01924920", name: "Engine Number", description: "Vehicle Engine No. in the logbook", isRequired: true, isUnique: true, validation: "MANUAL" },
  ];

  // Initialize requirementId with IDs from requirementsItems
  const initialValues = {
    requirementId: requirementsItems
      ? requirementsItems.map((item) => item.formCode).filter((id) => riskOptions.some((option) => option.id === id))
      : [],
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      // Build array of selected risk requirements
      const productRequirements = values.requirementId.map((reqId) => {
        const selected = riskOptions.find((item) => item.id === reqId);
        return {
          name: selected.name,
          description: selected.description,
          isRequired: selected.isRequired,
          isUnique: selected.isUnique,
          validationType: selected.validation,
          formCode: selected.id,
          type: "RISK",
        };
      });

      // Send all requirements in one call
      await setup_product_requirements({
        productSystemRef,
        productRequirements,
      });

      show_toast("Risk Requirement(s) added successfully!", "success");
      refetchProductDetails?.();
      resetForm();
    } catch (error) {
      show_toast("Error adding Risk requirement: " + error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 bg-white rounded shadow-sm my-4">
      <h4 className="mb-4">Add Risk Requirement</h4>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize // This ensures the form reinitializes when requirementsItems changes
      >
        {({ isSubmitting }) => (
          <Form className="mb-3">
            <div className="mb-3">
              <label className="form-label fw-semibold">
                Select Risk Requirements
              </label>
              <div className="d-flex flex-column gap-2">
                {riskOptions.map((item) => (
                  <label key={item.id} className="form-check">
                    <Field
                      type="checkbox"
                      name="requirementId"
                      value={item.id}
                      className="form-check-input"
                    />
                    <span className="form-check-label">{item.name}</span>
                  </label>
                ))}
              </div>
              <ErrorMessage
                name="requirementId"
                component="div"
                className="invalid-feedback d-block"
              />
            </div>

            <button
              type="submit"
              className="btn btn-success px-4 py-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Adding..." : "Add Risk Requirement(s)"}
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
}